// eslint-disable-next-line import/no-named-as-default
import Icon from "@mdi/react";
import { useCallback, useEffect, useRef, useState } from "react";

import { Error } from "@material-ui/icons";
import { mdiAccount, mdiAccountMultiple, mdiCheck, mdiClose, mdiDomain } from "@mdi/js";
import { Button, Input, Select } from "antd";
import styled from "styled-components";

import { ForecastFolder } from "models/UserArpsModel";
import { ProjectWithPermission } from "models/projects";

import { Tooltip } from "components/base";
import {
  useSelectedProject,
  useSelectedProjectPermissions
} from "components/project/projects/hooks";
import { useProjectListQuery } from "components/project/projects/queries";

import { useFolderSaveAs } from "./hooks/useFolderSaveAs";

const { Option } = Select;

interface FolderSaveAsProps {
  isOpen: boolean;
  selectedFolder: ForecastFolder;
  onCancel: () => void;
}

export function FolderSaveAs({ isOpen, selectedFolder, onCancel }: FolderSaveAsProps) {
  const { selectedProject } = useSelectedProject();
  const projectPermissions = useSelectedProjectPermissions();

  const [saveAsTitle, setSaveAsTitle] = useState("");
  const [saveAsToProjectId, setSaveAsToProjectId] = useState<string>("");
  const [nameError, setNameError] = useState<string>("");
  const [projects, setProjects] = useState<ProjectWithPermission[]>([]);

  const saveAsInputRef = useRef<HTMLInputElement>(null);

  const { handleSave, isCloning } = useFolderSaveAs({
    selectedProject,
    selectedFolder,
    saveAsToProjectId,
    saveAsTitle,
    onSuccess: onCancel,
    onError: setNameError
  });

  useProjectListQuery({
    onSuccess: (projects) => {
      const newProjects = projects.data.map((project) => ({
        ...project,
        permission: project.permission || projectPermissions
      }));
      setProjects(newProjects);
    }
  });

  // Initialize state when component opens
  useEffect(() => {
    if (isOpen) {
      setSaveAsTitle(`${selectedFolder?.title} Copy`);
      setSaveAsToProjectId(selectedProject?.projectId || "");
      setNameError("");

      // Select input text on next tick to ensure DOM is ready
      requestAnimationFrame(() => {
        saveAsInputRef.current?.select();
      });
    }
  }, [isOpen, selectedFolder?.title, selectedProject?.projectId]);

  // Ensure selected project is editable, otherwise select first editable project
  useEffect(() => {
    if (!projects?.length) return;

    const editableProjects = projects.filter((proj) => proj.permission?.canEditTypeWells);
    const isCurrentProjectEditable = editableProjects.some(
      (proj) => proj.projectId === saveAsToProjectId
    );

    if (!isCurrentProjectEditable && editableProjects.length > 0) {
      setSaveAsToProjectId(editableProjects[0].projectId);
    }
  }, [projects, saveAsToProjectId]);

  const handleCancel = useCallback(() => {
    setSaveAsTitle(`${selectedFolder?.title} Copy`);
    setNameError("");
    onCancel();
  }, [selectedFolder?.title, onCancel]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSaveAsTitle(e.target.value);
    setNameError("");
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        handleSave();
      } else if (e.key === "Escape") {
        handleCancel();
      }
    },
    [handleSave, handleCancel]
  );

  const editableProjects = ((projects as ProjectWithPermission[]) ?? []).filter(
    (proj) => proj.permission?.canEditTypeWells
  );

  return (
    <SaveAsWrapper>
      <InputWrapper $hasError={!!nameError}>
        <SaveAsInput
          ref={saveAsInputRef}
          value={saveAsTitle}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder="Enter folder name"
          status={nameError ? "error" : ""}
        />
        {nameError && (
          <ErrorMessage>
            <Error style={{ top: 3 }} /> {nameError}
          </ErrorMessage>
        )}
      </InputWrapper>
      <Select
        data-testid="folder-save-as-project-selector"
        value={saveAsToProjectId}
        onChange={(val) => setSaveAsToProjectId(val)}
        placeholder="Select project">
        {editableProjects.map((project) => (
          <Option
            key={project.projectId}
            value={project.projectId}
            label={project.projectName}>
            <OptionWrapper>
              <span role="img">
                {project.projectType === "Personal" ? (
                  <Icon path={mdiAccount} size={1.1} />
                ) : project.projectType === "Shared" ? (
                  <Icon path={mdiAccountMultiple} size={1.1} />
                ) : (
                  <Icon path={mdiDomain} size={1.1} />
                )}
              </span>
              {project.projectName}
            </OptionWrapper>
          </Option>
        ))}
      </Select>
      <Tooltip title="Cancel">
        <Button
          data-testid="folder-save-as-cancel-button"
          icon={<Icon path={mdiClose} size={1} color="#bbbbbb" />}
          type="text"
          onClick={handleCancel}
        />
      </Tooltip>
      <Tooltip title="Save As" destroyTooltipOnHide>
        <Button
          data-testid="folder-save-as-confirm-button"
          icon={<Icon path={mdiCheck} size={1} color="#02c7b0" />}
          type="text"
          onClick={handleSave}
          disabled={!saveAsTitle.trim() || isCloning}
          loading={isCloning}
        />
      </Tooltip>
    </SaveAsWrapper>
  );
}
const SaveAsWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  vertical-align: middle;
  width: 100%;
  gap: 5px;
  overflow: hidden;
  padding-left: 2px;

  .ant-btn-icon-only {
    width: 24px;
    height: 24px;
    margin-top: 0;
  }

  .ant-select-selector {
    max-width: 125px;
    min-width: 125px;
  }
`;

const InputWrapper = styled.div<{ $hasError: boolean }>`
  display: flex;
  flex-direction: column;
  min-height: ${(props) => (props.$hasError ? "48px" : "auto")};
`;

const ErrorMessage = styled.div`
  color: var(--color-danger);
  font-size: 12px;
  font-weight: var(--fontWeightMedium);
  margin-top: 2px;
`;

const SaveAsInput = styled(Input)`
  height: 25px;
  border: 1px solid black;
  background-color: white;
  max-width: 330px;
  min-width: 330px;
  margin-top: 3px;
`;

const OptionWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;

  span {
    padding: 2px 5px 0;
  }
`;

export default FolderSaveAs;
